"use client";

import React, { forwardRef, useCallback } from "react";
import Link from "next/link";
import { track } from "../core/client";
import { generateTraceId, setTraceId } from "../core/ids";
import { getConfig } from "../core/config";

/**
 * 클릭 추적이 가능한 링크 컴포넌트
 */

export interface TrackedLinkProps {
  href: string;
  asTrace?: boolean; // trace ID 생성 여부
  itemspec?: ItemSpec; // 클릭 대상 아이템 정보
  fromListId?: string; // 출발 리스트 ID
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  [key: string]: any;
}

export interface ItemSpec {
  kind?:
    | "stock"
    | "report"
    | "strategy"
    | "topic"
    | "newsrader"
    | "list_more"
    | "section_nav";
  ticker?: string;
  market?: string;
  cat?: "stock" | "index" | "crypto"; // stock용 카테고리
  report_id?: number;
  strategy_id?: string;
  topic_id?: number;
  newsrader_id?: number; // newsrader용
  list_id?: string; // list_more용
  section_id?: string; // section_nav용
  nav_key?: string; // section_nav용
  rank?: number;
  search_intent_id?: string; // 검색을 통한 클릭인 경우
  [key: string]: any;
}

/**
 * 추적 링크 컴포넌트
 */
export const TrackedLink = forwardRef<HTMLAnchorElement, TrackedLinkProps>(
  (
    {
      href,
      asTrace = false,
      itemspec,
      fromListId,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        const config = getConfig();

        // 사용자 정의 onClick 먼저 실행
        if (onClick) {
          onClick(e);
          // 이벤트가 preventDefault 되었으면 추적하지 않음
          if (e.defaultPrevented) return;
        }

        // 자동 클릭 추적이 비활성화되어 있으면 스킵
        if (!config.autoTracking.clicks) return;

        let traceId: string | undefined;

        // trace ID 생성 (asTrace가 true인 경우)
        if (asTrace) {
          traceId = generateTraceId();
          setTraceId(href, traceId);
        }

        // 클릭 이벤트 전송
        if (itemspec) {
          sendClickEvent(itemspec, fromListId, traceId);
        }
      },
      [href, asTrace, itemspec, fromListId, onClick]
    );

    return (
      <Link href={href} ref={ref} {...props} onClick={handleClick}>
        {children}
      </Link>
    );
  }
);

TrackedLink.displayName = "TrackedLink";

/**
 * 클릭 이벤트 전송
 */
function sendClickEvent(
  itemspec: ItemSpec,
  fromListId?: string,
  traceId?: string
) {
  const { kind, rank, ...props } = itemspec;

  if (!kind) {
    console.warn("TrackedLink: itemspec.kind is required for click tracking");
    return;
  }

  let eventName: string;
  let eventProps: any = {
    from_list_id: fromListId,
    rank_pos: rank,
    trace_id: traceId,
    ...props,
  };

  // 아이템 종류에 따른 이벤트명 결정
  switch (kind) {
    case "stock":
      eventName = "stock_click";
      if (!props.ticker || !props.market) {
        console.warn(
          "TrackedLink: ticker and market are required for stock_click"
        );
        return;
      }
      // stock은 cat, search_intent_id 포함
      eventProps = {
        ticker: props.ticker,
        market: props.market,
        cat: props.cat,
        from_list_id: fromListId,
        rank_pos: rank,
        trace_id: traceId,
        search_intent_id: props.search_intent_id,
      };
      break;
    case "report":
      eventName = "report_click";
      if (!props.report_id) {
        console.warn("TrackedLink: report_id is required for report_click");
        return;
      }
      // report는 search_intent_id 포함
      eventProps = {
        report_id: props.report_id,
        from_list_id: fromListId,
        rank_pos: rank,
        trace_id: traceId,
        search_intent_id: props.search_intent_id,
      };
      break;
    case "newsrader":
      eventName = "newsrader_click";
      if (!props.ticker || !props.newsrader_id) {
        console.warn(
          "TrackedLink: ticker and newsrader_id are required for newsrader_click"
        );
        return;
      }
      eventProps = {
        ticker: props.ticker,
        newsrader_id: props.newsrader_id,
        from_list_id: fromListId,
        rank_pos: rank,
        trace_id: traceId,
      };
      break;
    case "strategy":
      eventName = "strategy_click";
      if (!props.strategy_id) {
        console.warn("TrackedLink: strategy_id is required for strategy_click");
        return;
      }
      break;
    case "topic":
      eventName = "topic_click";
      if (!props.topic_id) {
        console.warn("TrackedLink: topic_id is required for topic_click");
        return;
      }
      break;
    case "list_more":
      eventName = "list_more_click";
      if (!props.list_id) {
        console.warn("TrackedLink: list_id is required for list_more_click");
        return;
      }
      // list_more는 from_list_id 불필요 (list_id가 이미 있음)
      eventProps = {
        list_id: props.list_id,
        trace_id: traceId,
      };
      break;
    case "section_nav":
      eventName = "section_nav_click";
      // section_nav는 section_id와 nav_key 모두 optional
      eventProps = {
        section_id: props.section_id,
        nav_key: props.nav_key,
        trace_id: traceId,
      };
      break;
    default:
      console.warn("Unknown item kind for click:", kind);
      return;
  }

  track(eventName as any, eventProps);
}

/**
 * 일반 버튼/요소에 클릭 추적을 추가하는 훅
 */
export function useClickTracking(itemspec: ItemSpec, fromListId?: string) {
  const handleClick = useCallback(
    (e: React.MouseEvent, generateTrace = false) => {
      const config = getConfig();
      if (!config.autoTracking.clicks) return;

      let traceId: string | undefined;

      if (generateTrace) {
        traceId = generateTraceId();
        // trace 저장은 대상 URL이 있을 때만 가능
        // 일반 버튼의 경우 별도 처리 필요
      }

      sendClickEvent(itemspec, fromListId, traceId);
    },
    [itemspec, fromListId]
  );

  return { onClick: handleClick };
}

/**
 * 로고 클릭 추적 컴포넌트 (홈 네비게이션)
 */
export interface TrackedLogoProps {
  href?: string;
  asTrace?: boolean; // trace_id 생성 여부
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedLogo({
  href = "/",
  asTrace = true, // 기본값: trace_id 생성
  children,
  ...props
}: TrackedLogoProps) {
  const handleClick = useCallback(() => {
    const config = getConfig();
    if (!config.autoTracking.clicks) return;

    let traceId: string | undefined;

    // trace ID 생성
    if (asTrace) {
      traceId = generateTraceId();
      setTraceId(href, traceId);
    }

    // nav_home_click 이벤트 전송
    track("nav_home_click", {
      trace_id: traceId,
    });
  }, [href, asTrace]);

  return (
    <Link href={href} {...props} onClick={handleClick}>
      {children}
    </Link>
  );
}

/**
 * 리스트 더보기 링크 컴포넌트
 *
 * @example
 * ```tsx
 * <TrackedListMoreLink href="/ranking" listId="explore_hot_industries">
 *   더보기
 * </TrackedListMoreLink>
 * ```
 */
export interface TrackedListMoreLinkProps {
  href: string;
  listId: string; // 리스트/섹션 ID
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedListMoreLink({
  href,
  listId,
  asTrace = true,
  children,
  ...props
}: TrackedListMoreLinkProps) {
  return (
    <TrackedLink
      href={href}
      asTrace={asTrace}
      itemspec={{ kind: "list_more", list_id: listId }}
      {...props}
    >
      {children}
    </TrackedLink>
  );
}

/**
 * 섹션 네비게이션 링크 컴포넌트
 *
 * @example
 * ```tsx
 * <TrackedSectionNavLink
 *   href="/portfolio/new"
 *   sectionId="main_banner"
 *   navKey="portfolio_cta"
 * >
 *   포트폴리오 시작하기
 * </TrackedSectionNavLink>
 * ```
 */
export interface TrackedSectionNavLinkProps {
  href: string;
  sectionId?: string; // 섹션 ID (optional)
  navKey?: string; // 네비게이션 키 (optional)
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedSectionNavLink({
  href,
  sectionId,
  navKey,
  asTrace = true,
  children,
  ...props
}: TrackedSectionNavLinkProps) {
  return (
    <TrackedLink
      href={href}
      asTrace={asTrace}
      itemspec={{
        kind: "section_nav",
        section_id: sectionId,
        nav_key: navKey,
      }}
      {...props}
    >
      {children}
    </TrackedLink>
  );
}

/**
 * 종목 클릭 링크 컴포넌트
 *
 * @example
 * ```tsx
 * <TrackedStockLink
 *   href="/detail/AAPL"
 *   ticker="AAPL"
 *   market="us"
 *   cat="stock"
 *   fromListId="explore_hot_stocks"
 *   rank={0}
 * >
 *   Apple Inc.
 * </TrackedStockLink>
 * ```
 */
export interface TrackedStockLinkProps {
  href: string;
  ticker: string;
  market: "kr" | "us";
  cat?: "stock" | "index" | "crypto";
  fromListId?: string;
  rank?: number;
  searchIntentId?: string;
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedStockLink({
  href,
  ticker,
  market,
  cat,
  fromListId,
  rank,
  searchIntentId,
  asTrace = true,
  children,
  ...props
}: TrackedStockLinkProps) {
  return (
    <TrackedLink
      href={href}
      asTrace={asTrace}
      fromListId={fromListId}
      itemspec={{
        kind: "stock",
        ticker,
        market,
        cat,
        rank,
        search_intent_id: searchIntentId,
      }}
      {...props}
    >
      {children}
    </TrackedLink>
  );
}

/**
 * 리포트 클릭 링크 컴포넌트
 *
 * @example
 * ```tsx
 * <TrackedReportLink
 *   href="/report/123"
 *   reportId={123}
 *   fromListId="main_reports"
 *   rank={0}
 * >
 *   AI 시장 전망 리포트
 * </TrackedReportLink>
 * ```
 */
export interface TrackedReportLinkProps {
  href: string;
  reportId: number;
  fromListId?: string;
  rank?: number;
  searchIntentId?: string;
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedReportLink({
  href,
  reportId,
  fromListId,
  rank,
  searchIntentId,
  asTrace = true,
  children,
  ...props
}: TrackedReportLinkProps) {
  return (
    <TrackedLink
      href={href}
      asTrace={asTrace}
      fromListId={fromListId}
      itemspec={{
        kind: "report",
        report_id: reportId,
        rank,
        search_intent_id: searchIntentId,
      }}
      {...props}
    >
      {children}
    </TrackedLink>
  );
}

/**
 * 뉴스레이더 클릭 링크 컴포넌트
 *
 * @example
 * ```tsx
 * <TrackedNewsRaderLink
 *   href="/detail/AAPL?tab=news"
 *   ticker="AAPL"
 *   newsraderId={456}
 *   fromListId="main_news_rader"
 *   rank={0}
 * >
 *   애플 실적 발표 예정
 * </TrackedNewsRaderLink>
 * ```
 */
export interface TrackedNewsRaderLinkProps {
  href: string;
  ticker: string;
  newsraderId: number;
  fromListId?: string;
  rank?: number;
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  [key: string]: any;
}

export function TrackedNewsRaderLink({
  href,
  ticker,
  newsraderId,
  fromListId,
  rank,
  asTrace = true,
  children,
  ...props
}: TrackedNewsRaderLinkProps) {
  return (
    <TrackedLink
      href={href}
      asTrace={asTrace}
      fromListId={fromListId}
      itemspec={{
        kind: "newsrader",
        ticker,
        newsrader_id: newsraderId,
        rank,
      }}
      {...props}
    >
      {children}
    </TrackedLink>
  );
}

/**
 * 탭 클릭 추적 훅
 *
 * @deprecated 이 훅은 deprecated되었습니다. hooks.tsx의 useTabTracking을 사용하세요.
 * 새 훅은 탭 인덱스, 체류시간, repeat_click 등을 자동으로 관리합니다.
 *
 * 마이그레이션 예시:
 * ```
 * // Before
 * const { onTabClick } = useTabClick('main');
 * onTabClick('watchlist', 'discover');
 *
 * // After
 * const mainTabs = useTabTracking('main_tab_click', ['discover', 'watchlist', 'portfolio', 'feed', 'kb']);
 * mainTabs.trackClick('watchlist');
 * ```
 */
export function useTabClick(context: "main" | "stock" | "strategy") {
  const handleTabClick = useCallback(
    (toTab: string, fromTab?: string, contextData?: any) => {
      const baseProps = {
        to_tab: toTab,
        from_tab: fromTab || undefined,
      };

      let eventName: string;
      let eventProps: any = baseProps;

      switch (context) {
        case "main":
          eventName = "main_tab_click";
          break;
        case "stock":
          eventName = "stock_tab_click";
          eventProps = { ...baseProps, ...contextData };
          break;
        case "strategy":
          eventName = "strategy_tab_click";
          eventProps = { ...baseProps, ...contextData };
          break;
        default:
          console.warn("Unknown tab context:", context);
          return;
      }

      track(eventName as any, eventProps);
    },
    [context]
  );

  return { onTabClick: handleTabClick };
}

// 기타 추적 훅들은 hooks.tsx로 이동됨
// 하위 호환성을 위해 re-export
export {
  useSearchTracking,
  useTabTracking,
  useMarketTabTracking,
  useStrategyTracking,
  usePortfolioTracking,
} from "./hooks";
